import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NewSelect, NewInput, NewModal, MainContentPaddingTypes, NewTimePicker } from "../..";
import { css, glyphs } from "../../../Styles";
import { ButtonTypes, NewInputHandles } from "../../../Definitions";
import NewButton from "../../BaseComponents/Buttons/newButton";
import { ANIMAL_EVENT_NOTE_ID, useEvent } from "../../../Services/Agrisys_GqlDataCaller/EventsDataCalls/useEvents";
import { useAppSelector } from "../../../Store";
import { useAnimalById } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/AnimalDataSlices/animalsSlice";
import { Animal } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getAnimalCall";

interface AAddEventModal_Props {
  animalId: Animal["animal_id"];
  isModalOpen: boolean;
  closeModal: () => void;
}

export default function AddEventModal({ animalId, isModalOpen, closeModal }: AAddEventModal_Props) {
  const { t } = useTranslation()
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const { createEvent } = useEvent(farm)
  const {entity: animal, isLoading} = useAnimalById(animalId); 
  const [message, setMessage] = useState<string | null>(null)
  
  const timePickerValueRef = useRef<{ hour: string | undefined, minute: string | undefined }>({ hour: undefined, minute: undefined });
  const dateValueRef = useRef<NewInputHandles | null>(null);
  const noteValueRef = useRef<NewInputHandles | null>(null);

  const createNewEvent = () => {
    
    const date = dateValueRef.current?.value;
    const hour = timePickerValueRef.current.hour ?? "00";
    const minute = timePickerValueRef.current.minute ?? "00";
    const type = ANIMAL_EVENT_NOTE_ID;
    const note = noteValueRef.current?.value;

    if (!date) {
      setMessage(t("events.dateMissing", { defaultValue: "date missing" }))
      setTimeout(() => setMessage(""), 2000);
    }
    else if (!type) {
      setMessage(t("events.typeMissing", { defaultValue: "type missing" }))
      setTimeout(() => setMessage(""), 2000);
    }
    else if (!note) {
      setMessage(t("events.noteMissing", { defaultValue: "note missing" }))
      setTimeout(() => setMessage(""), 2000);
    }

    const timestamp = new Date(
      `${date}T${hour.padStart(2, "0")}:${minute.padStart(2, "0")}:00`
    ).getTime();

    createEvent(animalId, type, timestamp / 1000, String(note));
    closeModal()
    setTimeout(() => {
      setMessage(null);
    }, 2000);
  }

  return (
    <NewModal
      modalState={isModalOpen}
      overrides={{
        mainContentPadding: MainContentPaddingTypes.secondary
      }}
      header={t("events.addEvent", { defaultValue: "add animal" })}
      subHeader={ isLoading || animal === undefined ? "Loading..."  :`${t("to", {defaultValue: "to"})} ${t("events.animal", {defaultValue: "animal"})} ${animal?.animal_number} (${animal.responder_number})` }
      predefinedButtons={{
        onClose: closeModal
      }}
      mainContent={
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "280px", margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <NewInput
              ref={dateValueRef}
              header={t("events.eventDate", {defaultValue: "date"})}
              valueOverrides={{ valueType: 'date' }}
              // onChange={(e) => dispatch({report: {startDate: new Date(e).getTime()}})}
              isRequired
              isClearable
              fieldOverrides={{fieldWidth: "121px"}}
            />
            <NewTimePicker
              valueRef={timePickerValueRef}
              header={t("events.eventTime", { defaultValue: "time" })}
              background
            />
          </div>
          <NewSelect 
            header={t("events.eventType", { defaultValue: "type" })}
            isRequired
            isDisabled
            options={[{ id: 1, label: "Note" }]}
            valueOverrides={{defaultValue: {id: 1, label: "Note"}}}
          />
          <NewInput 
            ref={noteValueRef}
            header={t("events.eventNote", { defaultValue: "note" })}
            isRequired
            isClearable
          />
        </div>
      }
      footerContent={          <NewButton 
        content={t("buttons.create", {defaultValue: "create"})}
        buttonType={ButtonTypes.primaryButton}
        onClick={createNewEvent}
      />}
      footerMessage={message ?? ""}
    />
  );
}